import React, { memo, useCallback, FC, useState, useEffect } from 'react'

import { AdProgress } from '@/components/ProgressBar/AdProgress'
import { ProgressBar } from '@/components/ProgressBar/ProgressBar'
import {
  eventObject,
  eventAction,
  videoPlayerNames,
  analyticsVideoTypes,
} from '@/constants/analytics'
import { icons } from '@/constants/icons'
import { useRewindVideo } from '@/hooks/useRewindVideo'
import { Keys } from '@/platform/platform'
import { useAdsStore } from '@/store/useAdsStore'
import { AnalyticsVideoDataType } from '@/types/analyticsTypes'
import { PlaybackControlsProps } from '@/types/componentsPropsTypes'
import { eventTypes } from '@/types/servicesTypes/configTypes'
import { getEpgObject, sendAnalyticsEvent } from '@/utils/analyticsUtils'
import { isWebos } from '@/utils/utils'

import { PlayImage, FocusButtonNode, Box } from './styles'

const PlaybackControlsRaw: FC<PlaybackControlsProps> = ({
  videoElementRef,
  videoData,
  setIsVideoPlaying,
  onExit,
  currentEvent,
  isVideoPlaying,
  isVideoStarted,
  setDisplayLiveChannels,
  displayVideoControls,
  displayLiveChannels,
  focusIdPrefix,
}) => {
  const adProgression = useAdsStore((state) => state.adProgression)

  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const { seekBack, seekForward } = useRewindVideo(
    videoElementRef,
    setCurrentTime,
  )

  const isWebOs = isWebos()
  const isChannel = videoData?.type === eventTypes.CHANNEL
  const seekIsAvailable =
    !isChannel && displayVideoControls && !displayLiveChannels && !adProgression

  const handleTimeUpdate = useCallback(() => {
    setDuration(videoElementRef?.current?.duration || 0)
    setCurrentTime(videoElementRef?.current?.currentTime || 0)
  }, [videoElementRef])

  useEffect(() => {
    const { current } = videoElementRef
    current?.addEventListener('timeupdate', handleTimeUpdate)

    return () => {
      current?.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [handleTimeUpdate, videoElementRef])

  const handleVideoAnalyticsEvent = useCallback(
    (action: string) => {
      const video: AnalyticsVideoDataType = {
        id: videoData?.id,
        link: videoData?.url,
      }

      if (action === eventAction.START_CLICK) {
        const epg = currentEvent ? getEpgObject(currentEvent) : undefined
        video.name = videoData?.media_name || videoData?.id
        video.playerName = videoPlayerNames.HLS
        video.type = analyticsVideoTypes.VOD
        video.length = isChannel ? '0' : videoElementRef.current?.duration
        video.epg = epg
      }

      sendAnalyticsEvent({
        object: eventObject.VIDEO,
        action,
        video,
      })
    },
    [videoData, currentEvent],
  )

  const play = useCallback(
    (e?: LRUDEvent) => {
      e && e.stopPropagation()
      videoElementRef.current?.play()
      setIsVideoPlaying(true)
      if (isVideoStarted) {
        handleVideoAnalyticsEvent(eventAction.PLAY)
      } else {
        handleVideoAnalyticsEvent(eventAction.START_CLICK)
      }
    },
    [
      videoElementRef,
      isVideoStarted,
      setIsVideoPlaying,
      handleVideoAnalyticsEvent,
    ],
  )

  const pause = useCallback(
    (e?: LRUDEvent) => {
      e && e.stopPropagation()
      if (!isVideoStarted) {
        return
      }
      videoElementRef.current?.pause()
      setIsVideoPlaying(false)
      handleVideoAnalyticsEvent(eventAction.PAUSE)
    },
    [
      videoElementRef,
      setIsVideoPlaying,
      isVideoStarted,
      handleVideoAnalyticsEvent,
    ],
  )

  const togglePlayPause = useCallback(() => {
    if (!videoElementRef.current) {
      return
    }
    videoElementRef.current.paused ? play() : pause()
  }, [play, pause, videoElementRef])

  useEffect(() => {
    const keyPressHandler = (e: KeyboardEvent) => {
      switch (e.keyCode) {
        case Keys.MEDIA_PLAY:
          return play()
        case Keys.MEDIA_PAUSE:
          return pause()
        case Keys.MEDIA_STOP:
          pause()
          return isWebOs && onExit()
        case Keys.MEDIA_PLAY_PAUSE:
          return togglePlayPause()
      }
    }

    window.addEventListener('keydown', keyPressHandler)
    return () => {
      window.removeEventListener('keydown', keyPressHandler)
    }
  }, [isWebOs, play, pause, togglePlayPause])

  const onSeekBack = () => seekIsAvailable && seekBack()
  const onSeekForward = () => seekIsAvailable && seekForward()

  return (
    <Box $marginBottom={20}>
      <div>
        <FocusButtonNode
          focusId={focusIdPrefix + 'section_1'}
          elementType="section"
          onSelected={() => {
            if (displayVideoControls) {
              isVideoPlaying ? pause() : play()
            }
          }}
          onDown={setDisplayLiveChannels}
          onLeft={onSeekBack}
          onRight={onSeekForward}
        >
          <PlayImage
            src={isVideoPlaying ? icons.controls.pause : icons.controls.play}
          />
        </FocusButtonNode>
      </div>
      {seekIsAvailable && (
        <ProgressBar currentTime={currentTime} duration={duration} />
      )}
      {adProgression && (
        <AdProgress
          currentTime={adProgression.currentTime}
          duration={adProgression.duration}
          currentSegment={adProgression.adPosition}
          numberOfSegments={adProgression.totalAds}
          lineColor={'#ffffff'}
        />
      )}
    </Box>
  )
}

export const PlaybackControls = memo(PlaybackControlsRaw)
